import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { api, putUpload, type EventRow, type StageRow } from '../api'
import { Loading, LOADING_GIF } from '../Loading'
import { ManualCropper } from '../components/ManualCropper'

const KINDS: { value: string; label: string }[] = [
  { value: 'player_list', label: 'Player List / 參賽名單' },
  { value: 'pairings', label: 'Pairings / 對陣' },
  { value: 'round_results', label: 'Round Results / 輪結果' },
  { value: 'standings', label: 'Standings / 積分榜' },
  { value: 'top_cut', label: 'Top Cut' },
  { value: 'final', label: 'Final / 最終名次' },
]

const KIND_LABEL: Record<string, string> = {
  player_list: '參賽者名單',
  pairings: '對陣表',
  round_results: '輪次結果',
  standings: '積分榜',
  top_cut: 'Top Cut',
  final: '最終名次',
}

const STAGE_FLOW_ORDER: Record<string, number> = {
  pairings: 10,
  round_results: 20,
  standings: 30,
  top_cut: 40,
  final: 50,
  player_list: 90,
}

function stageFlowRank(kind: string): number {
  return STAGE_FLOW_ORDER[kind] ?? 999
}

function parseWldRecord(record: string | undefined): { w: number; l: number; d: number } | null {
  const text = String(record ?? '').trim()
  if (!text) return null
  const m = text.match(/(\d+)\s*\/\s*(\d+)\s*\/\s*(\d+)/)
  if (!m) return null
  return { w: Number(m[1]), l: Number(m[2]), d: Number(m[3]) }
}

export function AdminEventPage() {
  const { id } = useParams()
  const eventId = id ?? ''
  const [user, setUser] = useState<{ id: string; role: string } | null | undefined>(undefined)
  const [event, setEvent] = useState<EventRow | null>(null)
  const [eventLoaded, setEventLoaded] = useState(false)
  const [stages, setStages] = useState<StageRow[]>([])
  const [allAdmins, setAllAdmins] = useState<Array<{ id: string; username: string; role: string }>>([])
  const [assignedAdmins, setAssignedAdmins] = useState<
    Array<{ assignmentId: string; userId: string; username: string; role: string; assignedBy: string }>
  >([])
  const [assignTarget, setAssignTarget] = useState('')
  const [kind, setKind] = useState('player_list')
  const [round, setRound] = useState<number | ''>('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [stageKindFilter, setStageKindFilter] = useState('all')
  const [stageStatusFilter, setStageStatusFilter] = useState('all')
  const [stageQuery, setStageQuery] = useState('')


  const publicUrl = useMemo(() => `${window.location.origin}/e/${event?.slug ?? ''}`, [event?.slug])

  useEffect(() => {
    void api
      .me()
      .then((r) => setUser(r.user ? { id: r.user.id, role: r.user.role } : null))
      .catch(() => setUser(null))
  }, [])

  useEffect(() => {
    if (!eventId || !user) return
    setEventLoaded(false)
    void Promise.all([api.event(eventId), api.stages(eventId)])
      .then(([ev, st]) => {
        setEvent(ev.event)
        setStages(st.stages)
        setTitleDraft(ev.event.title)
      })

      .catch(() => {
        setEvent(null)
        setStages([])
      })
      .finally(() => setEventLoaded(true))
  }, [eventId, user])

  useEffect(() => {
    if (!eventId || user?.role !== 'super_admin') return
    void Promise.all([api.listAdmins(), api.eventAdmins(eventId)])
      .then(([a, m]) => {
        setAllAdmins(a.admins)
        setAssignedAdmins(m.admins)
      })
      .catch(() => {
        setAllAdmins([])
        setAssignedAdmins([])
      })
  }, [eventId, user?.role])

  useEffect(() => {
    setStageKindFilter('all')
    setStageStatusFilter('all')
    setStageQuery('')
  }, [eventId])

  async function refresh() {
    const st = await api.stages(eventId)
    setStages(st.stages)
  }

  async function addStage(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy('add')
    try {
      await api.createStage(eventId, {
        kind,
        round: round === '' ? undefined : Number(round),
      })
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : '失敗')
    } finally {
      setBusy(null)
    }
  }

  const sortedStages = useMemo(() => [...stages].sort((a, b) => b.sortIndex - a.sortIndex), [stages])

  const stageSummary = useMemo(() => {
    const byKind = new Map<string, number>()
    let published = 0
    let draft = 0
    let parseErrors = 0
    let withUploads = 0
    for (const s of stages) {
      byKind.set(s.kind, (byKind.get(s.kind) ?? 0) + 1)
      if (s.status === 'published') published += 1
      if (s.status === 'draft') draft += 1
      if (s.parseError) parseErrors += 1
      if ((s.uploadCount ?? 0) > 0 || s.r2Key) withUploads += 1
    }
    return { total: stages.length, published, draft, parseErrors, withUploads, byKind }
  }, [stages])

  const kindOptions = useMemo(() => {
    const allKinds = Array.from(stageSummary.byKind.keys())
    return allKinds.sort((a, b) => stageFlowRank(a) - stageFlowRank(b) || a.localeCompare(b))
  }, [stageSummary.byKind])

  const filteredStages = useMemo(() => {
    const q = stageQuery.trim().toLowerCase()
    return sortedStages.filter((s) => {
      if (stageKindFilter !== 'all' && s.kind !== stageKindFilter) return false
      if (stageStatusFilter !== 'all' && s.status !== stageStatusFilter) return false
      if (!q) return true
      const label = KIND_LABEL[s.kind] ?? s.kind
      const roundText = s.round == null ? '' : `round ${s.round}`
      return `${label} ${s.kind} ${roundText} ${s.sortIndex}`.toLowerCase().includes(q)
    })
  }, [sortedStages, stageKindFilter, stageStatusFilter, stageQuery])

  const workflowSections = useMemo(() => {
    const byRound = new Map<number, StageRow[]>()
    const baselineRows: StageRow[] = []
    for (const s of filteredStages) {
      if (typeof s.round === 'number' && Number.isFinite(s.round)) {
        const arr = byRound.get(s.round) ?? []
        arr.push(s)
        byRound.set(s.round, arr)
      } else {
        baselineRows.push(s)
      }
    }
    const sortRows = (rows: StageRow[]) =>
      [...rows].sort((a, b) => {
        const rankDiff = stageFlowRank(a.kind) - stageFlowRank(b.kind)
        if (rankDiff !== 0) return rankDiff
        return b.sortIndex - a.sortIndex
      })
    const roundSections = Array.from(byRound.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([round, rows]) => ({
        key: `round-${round}`,
        title: `Round ${round}`,
        rows: sortRows(rows),
      }))
    if (baselineRows.length > 0) {
      roundSections.push({
        key: 'baseline',
        title: '基礎資料 / 非輪次',
        rows: sortRows(baselineRows),
      })
    }
    return roundSections
  }, [filteredStages])

  const hasStageFilters = stageKindFilter !== 'all' || stageStatusFilter !== 'all' || stageQuery.trim().length > 0

  if (user === undefined) return <Loading />
  if (!user) return <Navigate to="/admin/login" replace />
  if (!eventId) return <Navigate to="/admin" replace />
  if (!eventLoaded) return <Loading label="Loading event" />
  if (!event) return <div className="card">找不到活動或你沒有權限。</div>

  return (
    <div className="stack">
      <div className="row">
        <Link className="btn" to="/admin">
          返回
        </Link>
        <Link className="btn" to={`/e/${event!.slug}`} target="_blank" rel="noreferrer">
          預覽公開頁
        </Link>
      </div>
      <div className="card admin-event-header">
        <div className="admin-event-header-left">
          <div className="admin-event-title-row">
            {isEditingTitle ? (
              <div className="row" style={{ flex: 1, minWidth: 300 }}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoFocus
                />
                <button
                  className="btn primary btn-sm"
                  disabled={busy === 'save-title' || !titleDraft.trim()}
                  onClick={async () => {
                    if (!titleDraft.trim() || !event) return
                    setBusy('save-title')
                    try {
                      await api.patchEvent(event.id, { title: titleDraft.trim() })
                      setEvent({ ...event, title: titleDraft.trim() })
                      setIsEditingTitle(false)
                    } catch (err) {
                      setError(err instanceof Error ? err.message : '儲存標題失敗')
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  儲存
                </button>
                <button className="btn btn-sm" onClick={() => { setIsEditingTitle(false); setTitleDraft(event?.title ?? ''); }}>
                  取消
                </button>
              </div>
            ) : (
              <>
                <h1 style={{ margin: 0 }}>{event!.title}</h1>
                {user?.role === 'super_admin' && (
                  <button
                    className="btn btn-sm"
                    style={{ padding: '2px 8px', fontSize: '0.75rem', opacity: 0.7 }}
                    onClick={() => {
                      setTitleDraft(event!.title)
                      setIsEditingTitle(true)
                    }}
                  >
                    編輯名稱
                  </button>
                )}
                <a className="admin-event-link" href={publicUrl} target="_blank" rel="noreferrer">
                  公開連結
                </a>
              </>
            )}
          </div>
        </div>

        <a className="admin-event-qr" href={publicUrl} target="_blank" rel="noreferrer" aria-label="公開頁 QR Code">
          <img alt="QR" width={110} height={110} src={`/api/qr?url=${encodeURIComponent(publicUrl)}`} />
        </a>
      </div>

      {user?.role === 'super_admin' ? (
        <div className="card stack">
          <h2 style={{ margin: 0 }}>指派活動管理員</h2>
          <div className="row">
            <select className="input" value={assignTarget} onChange={(e) => setAssignTarget(e.target.value)}>
              <option value="">選擇 admin 帳號</option>
              {allAdmins.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
            <button
              className="btn"
              type="button"
              disabled={!assignTarget || !!busy}
              onClick={async () => {
                setBusy('assign-admin')
                setError(null)
                try {
                  await api.assignEventAdmin(eventId, assignTarget)
                  const m = await api.eventAdmins(eventId)
                  setAssignedAdmins(m.admins)
                  setAssignTarget('')
                } catch (err) {
                  setError(err instanceof Error ? err.message : '指派失敗')
                } finally {
                  setBusy(null)
                }
              }}
            >
              指派
            </button>
          </div>
          <div className="stack">
            {assignedAdmins.length === 0 ? <div className="muted">暫未指派任何 admin</div> : null}
            {assignedAdmins.map((a) => (
              <div key={a.assignmentId} className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <strong>{a.username}</strong>
                  <span className="muted">（{a.role}）</span>
                </div>
                <button
                  className="btn"
                  type="button"
                  disabled={!!busy}
                  onClick={async () => {
                    setBusy(`unassign:${a.userId}`)
                    setError(null)
                    try {
                      await api.unassignEventAdmin(eventId, a.userId)
                      const m = await api.eventAdmins(eventId)
                      setAssignedAdmins(m.admins)
                    } catch (err) {
                      setError(err instanceof Error ? err.message : '移除失敗')
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  移除
                </button>
              </div>
            ))}
          </div>
          {error ? <div style={{ color: '#fecaca' }}>{error}</div> : null}
        </div>
      ) : null}

      <div className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>新增階段</h2>
          {error ? <div style={{ color: '#fecaca' }}>{error}</div> : null}
        </div>
        <form className="row admin-compact-form" onSubmit={addStage}>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ maxWidth: 360 }}>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <input
            className="input admin-round-input"
            type="number"
            placeholder="Round"
            value={round}
            onChange={(e) => setRound(e.target.value === '' ? '' : Number(e.target.value))}
            min={0}
            max={99}
          />
          <button className="btn primary btn-sm" type="submit" disabled={busy === 'add'}>
            新增
          </button>
        </form>
        <div className="muted" style={{ marginTop: '-0.25rem' }}>
          輪次只需填數字（pairings / round_results 建議填）
        </div>
      </div>

      <div className="card stack">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>階段工作台</h2>
          <div className="row" style={{ gap: '0.35rem' }}>
            <span className="round-stat-pill">總數 {stageSummary.total}</span>
            <span className="round-stat-pill">已發佈 {stageSummary.published}</span>
            <span className="round-stat-pill">草稿 {stageSummary.draft}</span>
            <span className={`round-stat-pill ${stageSummary.parseErrors > 0 ? 'danger' : ''}`}>解析錯誤 {stageSummary.parseErrors}</span>
            <span className="round-stat-pill">有上載圖 {stageSummary.withUploads}</span>
          </div>
        </div>

        <div className="stage-filter-row">
          <select className="input stage-filter-input" value={stageKindFilter} onChange={(e) => setStageKindFilter(e.target.value)}>
            <option value="all">所有類型（{stageSummary.total}）</option>
            {kindOptions.map((k) => (
              <option key={k} value={k}>
                {(KIND_LABEL[k] ?? k)}（{stageSummary.byKind.get(k) ?? 0}）
              </option>
            ))}
          </select>
          <select className="input stage-filter-input" value={stageStatusFilter} onChange={(e) => setStageStatusFilter(e.target.value)}>
            <option value="all">所有狀態</option>
            <option value="draft">草稿</option>
            <option value="published">已發佈</option>
          </select>
          <input
            className="input stage-filter-input"
            placeholder="搜尋：round / 類型 / sort"
            value={stageQuery}
            onChange={(e) => setStageQuery(e.target.value)}
          />
          {hasStageFilters ? (
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => {
                setStageKindFilter('all')
                setStageStatusFilter('all')
                setStageQuery('')
              }}
            >
              清除篩選
            </button>
          ) : null}
        </div>

        <div className="stack">
          {workflowSections.length === 0 ? (
            <div className="muted">沒有符合目前篩選條件的階段。</div>
          ) : (
            workflowSections.map((group) => (
              <section key={group.key} className="stage-kind-group">
                <div className="stage-kind-group-head">
                  <h3 style={{ margin: 0 }}>{group.title}</h3>
                  <span className="pill">{group.rows.length} 個階段</span>
                </div>
                <div className="stack">
                  {group.rows.map((s) => (
                    <StageEditor
                      key={s.id}
                      eventSlug={event!.slug}
                      stage={s}
                      onRefresh={refresh}
                      busy={busy}
                      setBusy={setBusy}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function StageEditor({
  eventSlug,
  stage,
  onRefresh,
  busy,
  setBusy,
}: {
  eventSlug: string
  stage: StageRow
  onRefresh: () => Promise<void>
  busy: string | null
  setBusy: (v: string | null) => void
}) {
  const [jsonDraft, setJsonDraft] = useState(stage.parsedJson ?? '')
  const [msg, setMsg] = useState<string | null>(null)
  const [uploads, setUploads] = useState<Array<{ id: string; sortIndex: number }>>([])
  const [preview, setPreview] = useState<{ uploadId: string } | null>(null)
  const [croppingFile, setCroppingFile] = useState<File | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editorMode, setEditorMode] = useState<'table' | 'json'>('table')
  const [collapsed, setCollapsed] = useState(true)
  const [playerRows, setPlayerRows] = useState<
    Array<{ player_id: string; name: string; no?: string; date?: string; division?: string }>
  >([])
  const [pairingRows, setPairingRows] = useState<
    Array<{
      table: number
      p1_id: string
      p1_name: string
      p1_seat_or_suffix?: string
      p1_division?: string
      p1_record?: string
      p2_id?: string
      p2_name?: string
      p2_seat_or_suffix?: string
      p2_division?: string
      p2_record?: string
      is_bye?: boolean
    }>
  >([])
  const [resultRows, setResultRows] = useState<
    Array<{
      table: number
      p1_id: string
      p1_name: string
      p1_record?: string
      p1_is_winner?: boolean
      p2_id?: string
      p2_name?: string
      p2_record?: string
      p2_is_winner?: boolean
      is_bye?: boolean
    }>
  >([])
  const [resultShowAnomaliesOnly, setResultShowAnomaliesOnly] = useState(false)

  useEffect(() => {
    setJsonDraft(stage.parsedJson ?? '')
  }, [stage.parsedJson])

  useEffect(() => {
    setConfirmDelete(false)
  }, [stage.id])

  useEffect(() => {
    setResultShowAnomaliesOnly(false)
  }, [stage.id, stage.kind])

  useEffect(() => {
    if (stage.kind !== 'player_list') return
    // Always try to update table rows if either the stage data changed OR we just switched to table mode
    try {
      const source = (editorMode === 'table' ? (jsonDraft || stage.parsedJson) : stage.parsedJson) || 'null'
      const raw = JSON.parse(source) as unknown
      const obj = raw as { players?: unknown }
      const players = Array.isArray(obj?.players) ? (obj.players as any[]) : []
      const cleaned = players
        .filter((p) => p && typeof p === 'object')
        .map((p) => ({
          player_id: String((p as any).player_id ?? '').trim().match(/^\d+/)?.[0] ?? String((p as any).player_id ?? '').trim(),
          name: String((p as any).name ?? ''),
          no: (p as any).no != null ? String((p as any).no) : '',
          date: (p as any).date != null ? String((p as any).date) : '',
          division: (p as any).division != null ? String((p as any).division) : '',
        }))
      setPlayerRows(cleaned)
    } catch {
      // If JSON is invalid, don't clear the rows if we're just typing, 
      // but clear if it's a fresh stage or forced refresh.
    }
  }, [stage.id, stage.kind, stage.parsedJson, editorMode, jsonDraft])

  useEffect(() => {
    if (stage.kind !== 'pairings') return
    try {
      const source = (editorMode === 'table' ? (jsonDraft || stage.parsedJson) : stage.parsedJson) || 'null'
      const raw = JSON.parse(source) as any
      const rows = Array.isArray(raw?.pairings) ? raw.pairings : []
      const cleaned = rows
        .filter((r: any) => r && typeof r === 'object')
        .map((r: any) => ({
          table: Number(r.table ?? 0),
          p1_id: String(r.player1?.id ?? ''),
          p1_name: String(r.player1?.name ?? ''),
          p1_seat_or_suffix: r.player1?.seat_or_suffix != null ? String(r.player1.seat_or_suffix) : '',
          p1_division: r.player1?.division != null ? String(r.player1.division) : '',
          p1_record: r.player1?.record != null ? String(r.player1.record) : '',
          p2_id: r.player2?.id != null ? String(r.player2.id) : '',
          p2_name: r.player2?.name != null ? String(r.player2.name) : '',
          p2_seat_or_suffix: r.player2?.seat_or_suffix != null ? String(r.player2.seat_or_suffix) : '',
          p2_division: r.player2?.division != null ? String(r.player2.division) : '',
          p2_record: r.player2?.record != null ? String(r.player2.record) : '',
          is_bye: !!r.is_bye,
        }))
      setPairingRows(cleaned)
    } catch { /* ignore */ }
  }, [stage.id, stage.kind, stage.parsedJson, editorMode, jsonDraft])

  useEffect(() => {
    if (stage.kind !== 'round_results') return
    try {
      const source = (editorMode === 'table' ? (jsonDraft || stage.parsedJson) : stage.parsedJson) || 'null'
      const raw = JSON.parse(source) as any
      const rows = Array.isArray(raw?.matches) ? raw.matches : []
      const cleaned = rows
        .filter((r: any) => r && typeof r === 'object')
        .map((r: any) => ({
          table: Number(r.table ?? 0),
          p1_id: String(r.player1?.id ?? ''),
          p1_name: String(r.player1?.name ?? ''),
          p1_record: r.player1?.record != null ? String(r.player1.record) : '',
          p1_is_winner: r.player1?.is_winner === undefined ? undefined : !!r.player1.is_winner,
          p2_id: r.player2?.id != null ? String(r.player2.id) : '',
          p2_name: r.player2?.name != null ? String(r.player2.name) : '',
          p2_record: r.player2?.record != null ? String(r.player2.record) : '',
          p2_is_winner: r.player2?.is_winner === undefined ? undefined : !!r.player2.is_winner,
          is_bye: !!r.is_bye,
        }))
      setResultRows(cleaned)
    } catch { /* ignore */ }
  }, [stage.id, stage.kind, stage.parsedJson, editorMode, jsonDraft])

  const analyzedResultRows = useMemo(() => {
    const tableCount = new Map<number, number>()
    for (const row of resultRows) {
      const t = Number(row.table ?? 0)
      tableCount.set(t, (tableCount.get(t) ?? 0) + 1)
    }
    return resultRows.map((row, idx) => {
      const issues: string[] = []
      const table = Number(row.table ?? 0)
      const byeLike = !!row.is_bye || (!String(row.p2_id ?? '').trim() && !String(row.p2_name ?? '').trim())

      if (!Number.isFinite(table) || !Number.isInteger(table) || table < 0) {
        issues.push('table 格式錯誤')
      }
      if (!String(row.p1_id ?? '').trim()) issues.push('缺 p1 id')
      if (!String(row.p1_name ?? '').trim()) issues.push('缺 p1 name')
      if ((tableCount.get(table) ?? 0) > 1) issues.push(`table ${table} 重複`)

      if (byeLike) {
        if (table !== 0) issues.push('BYE 應為 table 0')
        if (row.p1_is_winner !== true) issues.push('BYE p1 應為勝方')
        if (row.p2_is_winner === true) issues.push('BYE 不應有 p2 勝方')
      } else {
        if (table <= 0) issues.push('非 BYE table 應為正整數')
        if (!String(row.p2_id ?? '').trim() && !String(row.p2_name ?? '').trim()) issues.push('缺 p2 玩家')
        if (row.p1_is_winner === true && row.p2_is_winner === true) issues.push('雙方同勝')
        if (row.p1_is_winner === undefined && row.p2_is_winner === undefined) issues.push('未標記勝負')
      }

      const p1 = parseWldRecord(row.p1_record)
      const p2 = parseWldRecord(row.p2_record)
      const isDoubleLoss = !byeLike && row.p1_is_winner === false && row.p2_is_winner === false
      if (isDoubleLoss && p1 && p2 && (p1.w !== p2.w || p1.l !== p2.l || p1.d !== p2.d)) {
        issues.push('雙敗但 record 不對稱')
      }

      return { row, idx, issues, isDoubleLoss }
    })
  }, [resultRows])

  const resultAnomalyCount = analyzedResultRows.filter((x) => x.issues.length > 0).length
  const resultDoubleLossCount = analyzedResultRows.filter((x) => x.isDoubleLoss).length
  const visibleResultRows = resultShowAnomaliesOnly
    ? analyzedResultRows.filter((x) => x.issues.length > 0)
    : analyzedResultRows

  const refreshUploads = () => {
    void api
      .stageUploads(stage.id)
      .then((r) => setUploads(r.uploads.map((u) => ({ id: u.id, sortIndex: u.sortIndex }))))
      .catch(() => setUploads([]))
  }

  useEffect(() => {
    refreshUploads()
  }, [stage.id, stage.uploadCount])

  return (
    <div className="card stack" style={{ borderColor: '#223155' }}>

      {/* ── 1. Stage Info (always visible, click to collapse) ──── */}
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        aria-expanded={!collapsed}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="stage-main-title">
            <span className="stage-main-caret" style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
            <span>{stage.round != null ? `Round ${stage.round} ` : ''}{KIND_LABEL[stage.kind] ?? stage.kind}</span>
          </div>
          <div className="stage-meta-row" onClick={(e) => e.stopPropagation()}>
            <span className="pill">{stage.status}</span>
            {((stage.uploadCount ?? 0) > 0 || stage.r2Key) ? (
              <span className="pill">🖼️ 已上載 {stage.uploadCount ?? 1} 張</span>
            ) : (
              <span className="pill">📭 未上載圖</span>
            )}
            {stage.kind === 'round_results' ? (
              <span className={`pill ${resultAnomalyCount > 0 ? 'pill-alert' : 'pill-ok'}`}>
                檢查：{resultAnomalyCount} 異常
              </span>
            ) : null}
          </div>
          {stage.parseError ? (
            <div style={{ color: '#fecaca', whiteSpace: 'pre-wrap', marginTop: '0.35rem', fontSize: '0.82rem' }}>⚠️ {stage.parseError}</div>
          ) : null}
        </div>
        <div className="stage-side-actions" onClick={(e) => e.stopPropagation()}>
          {msg && (
            <div className={`status-msg animated-status ${msg.includes('失敗') || msg.includes('錯誤') ? 'error' : 'success'}`}>
              {msg}
            </div>
          )}
          <Link className="btn btn-sm" to={`/e/${eventSlug}/${stage.id}`} target="_blank" rel="noreferrer">
            👁 預覽此階段
          </Link>
          <label className="stage-sort-chip">
            <span className="stage-sort-label">Sort</span>
            <input
              className="input input-sm stage-sort-input"
              type="number"
              defaultValue={stage.sortIndex}
              onBlur={async (e) => {
                const val = Number(e.target.value)
                if (val === stage.sortIndex) return
                try {
                  await api.patchStage(stage.id, { sortIndex: val })
                  await onRefresh()
                } catch {
                  // ignore sort patch errors in header quick edit
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            />
          </label>
        </div>
      </div>

      {/* ── Collapsible body ──────────────────────────────────── */}
      {!collapsed && (
        <>
      {croppingFile && (
        <ManualCropper
          file={croppingFile}
          onCancel={() => setCroppingFile(null)}
          onFinished={async (blobs) => {
            setCroppingFile(null)
            setBusy(`up:${stage.id}`)
            try {
              for (let i = 0; i < blobs.length; i++) {
                setMsg(`正在上傳裁切部分 ${i + 1}/${blobs.length}...`)
                const u = await api.uploadUrl(stage.id)
                const uploadFile = new File([blobs[i]], `manual-${Date.now()}-${i}.jpg`, { type: 'image/jpeg' })
                await putUpload(u.path, uploadFile)
              }
              setMsg('手動裁切上傳完成')
              await onRefresh()
              try {
                const r = await api.stageUploads(stage.id)
                setUploads(r.uploads.map((x) => ({ id: x.id, sortIndex: x.sortIndex })))
              } catch { /* ignore */ }
            } catch (err) {
              setMsg(err instanceof Error ? err.message : '上傳失敗')
            } finally {
              setBusy(null)
            }
          }}
        />
      )}

      {/* ── 2. 截圖 & AI 解析 ────────────────────────────────────── */}
      <hr style={{ borderColor: '#223155', margin: '0.25rem 0' }} />
      <div className="stack" style={{ gap: '0.6rem' }}>
        <div className="stage-section-title">🖼️ 截圖 &amp; AI 解析</div>
        <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.5 }}>
          💡 建議每次截圖保持在 <strong style={{ color: '#94a3b8' }}>10–15 桌</strong>左右。你可以<strong style={{ color: '#94a3b8' }}>上傳多張截圖</strong>，系統解析時會自動合併。
        </div>
        <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <label className="row" style={{ gap: '0.4rem', cursor: 'pointer' }}>
            <span className="btn btn-sm" style={{ pointerEvents: 'none' }}>🖼️ 原圖上載（不切割）</span>
            <input
              type="file"
              accept="image/*"
              disabled={!!busy}
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setMsg(null)
                setBusy(`up:${stage.id}`)
                try {
                  const u = await api.uploadUrl(stage.id)
                  await putUpload(u.path, file)
                  setMsg('原圖上載完成')
                  await onRefresh()
                  try {
                    const r = await api.stageUploads(stage.id)
                    setUploads(r.uploads.map((x) => ({ id: x.id, sortIndex: x.sortIndex })))
                  } catch { /* ignore */ }
                } catch (err) {
                  setMsg(err instanceof Error ? err.message : '上載失敗')
                } finally {
                  setBusy(null)
                  e.target.value = ''
                }
              }}
            />
          </label>

          <label className="row" style={{ gap: '0.4rem', cursor: 'pointer' }}>
            <span className="btn btn-sm" style={{ borderStyle: 'dashed', borderColor: 'var(--accent)' }}>✂️ 手動裁切上傳</span>
            <input
              type="file"
              accept="image/*"
              disabled={!!busy}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  setCroppingFile(file)
                  e.target.value = ''
                }
              }}
            />
          </label>

          <button
            className={`btn btn-sm ${busy === `parse:${stage.id}` ? 'busy' : ''}`}
            type="button"
            disabled={!!busy || !((stage.uploadCount ?? 0) > 0 || stage.r2Key)}
            onClick={async () => {
              setBusy(`parse:${stage.id}`)
              setMsg(null)
              let stopProgress = false
              const pollProgress = async () => {
                while (!stopProgress) {
                  try {
                    const p = await api.parseProgress(stage.id)
                    if (p.running && p.total > 0) {
                      setMsg(`解析中 ${p.done}/${p.total}（成功 ${p.succeeded}，失敗 ${p.failed}）`)
                    }
                  } catch {
                    // ignore polling errors during parse
                  }
                  await new Promise((r) => setTimeout(r, 800))
                }
              }
              const progressTask = pollProgress()
              try {
                const r = await api.parseStage(stage.id)
                const failures = r.summary.failures ?? []
                const failureText =
                  failures.length > 0
                    ? `；失敗 ${r.summary.failed} 張（${failures.slice(0, 2).map((f) => f.reason).join('；')}${failures.length > 2 ? '；...' : ''}）`
                    : ''
                setMsg(`解析完成（已存草稿）。已處理 ${r.summary.succeeded}/${r.summary.total} 張${failureText}`)
                await onRefresh()
              } catch (err) {
                setMsg(err instanceof Error ? err.message : '解析失敗')
              } finally {
                stopProgress = true
                await progressTask
                setBusy(null)
              }
            }}
          >
            {busy === `parse:${stage.id}` ? (
              <img src={LOADING_GIF} alt="Loading" style={{ width: 18, height: 18, imageRendering: 'pixelated' }} />
            ) : '🤖 解析（Vision）'}
          </button>

          {uploads.length > 0 ? (
            <div className="row" style={{ gap: '0.6rem', flexWrap: 'wrap' }}>
              {uploads.slice(0, 10).map((u, idx) => (
                <div key={u.id} className="stack" style={{ gap: '0.25rem', alignItems: 'center', background: '#1e293b', padding: '0.35rem', borderRadius: '0.4rem' }}>
                  <button type="button" className="thumb" onClick={() => setPreview({ uploadId: u.id })} disabled={!!busy} aria-label="查看上載圖片" style={{ margin: 0 }}>
                    <img alt="" src={`/api/stages/${stage.id}/uploads/${u.id}/image`} />
                  </button>
                  <div className="row" style={{ gap: '0.2rem' }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ padding: '0.1rem 0.3rem', fontSize: '0.7rem' }}
                      disabled={!!busy || idx === 0}
                      onClick={async () => {
                        const newOrder = [...uploads]
                        const [moved] = newOrder.splice(idx, 1)
                        newOrder.splice(idx - 1, 0, moved)
                        setBusy(`sort:${stage.id}`)
                        try {
                          await api.reorderUploads(stage.id, newOrder.map(u => u.id))
                          refreshUploads()
                        } catch (err) {
                          setMsg(err instanceof Error ? err.message : '排序失敗')
                        } finally { setBusy(null) }
                      }}
                    >
                      ◀
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ padding: '0.1rem 0.3rem', fontSize: '0.7rem' }}
                      disabled={!!busy || idx === uploads.length - 1}
                      onClick={async () => {
                        const newOrder = [...uploads]
                        const [moved] = newOrder.splice(idx, 1)
                        newOrder.splice(idx + 1, 0, moved)
                        setBusy(`sort:${stage.id}`)
                        try {
                          await api.reorderUploads(stage.id, newOrder.map(u => u.id))
                          refreshUploads()
                        } catch (err) {
                          setMsg(err instanceof Error ? err.message : '排序失敗')
                        } finally { setBusy(null) }
                      }}
                    >
                      ▶
                    </button>
                  </div>
                </div>
              ))}
              {uploads.length > 10 ? <span className="muted" style={{ fontSize: '0.8rem', alignSelf: 'center' }}>+{uploads.length - 10}</span> : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* ── 3. 發佈控制 ──────────────────────────────────────────── */}
      <hr style={{ borderColor: '#223155', margin: '0.25rem 0' }} />
      <div className="stack" style={{ gap: '0.6rem' }}>
        <div className="stage-section-title">🚀 發佈控制</div>
        <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <button
            className="btn primary btn-sm"
            type="button"
            disabled={!!busy || !(stage.parsedJson || stage.showImagesOnly)}
            onClick={async () => {
              setBusy(`pub:${stage.id}`)
              setMsg(null)
              try {
                await api.publishStage(stage.id)
                setMsg('已發佈')
                await onRefresh()
              } catch (err) {
                setMsg(err instanceof Error ? err.message : '發佈失敗')
              } finally {
                setBusy(null)
              }
            }}
          >
            ✅ 發佈
          </button>
          <button
            className="btn btn-sm"
            type="button"
            disabled={!!busy}
            onClick={async () => {
              setBusy(`unpub:${stage.id}`)
              setMsg(null)
              try {
                await api.unpublishStage(stage.id)
                setMsg('已改回草稿')
                await onRefresh()
              } catch (err) {
                setMsg(err instanceof Error ? err.message : '失敗')
              } finally {
                setBusy(null)
              }
            }}
          >
            ↩️ 撤回發佈
          </button>
          <div className="row" style={{ marginLeft: 'auto', gap: '0.5rem', alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: '0.8rem' }}>顯示方式：</span>
            <div className="row segmented-control">
              <button
                type="button"
                className={stage.displayMode === 'table' ? 'active' : ''}
                disabled={!!busy}
                onClick={async () => {
                  setBusy(`mode:${stage.id}`)
                  try {
                    await api.patchStage(stage.id, { displayMode: 'table' })
                    await onRefresh()
                  } catch { /* ignore */ } finally { setBusy(null) }
                }}
              >
                表格
              </button>
              <button
                type="button"
                className={stage.displayMode === 'images' ? 'active' : ''}
                disabled={!!busy}
                onClick={async () => {
                  setBusy(`mode:${stage.id}`)
                  try {
                    await api.patchStage(stage.id, { displayMode: 'images' })
                    await onRefresh()
                  } catch { /* ignore */ } finally { setBusy(null) }
                }}
              >
                圖片
              </button>
              <button
                type="button"
                className={stage.displayMode === 'both' ? 'active' : ''}
                disabled={!!busy}
                onClick={async () => {
                  setBusy(`mode:${stage.id}`)
                  try {
                    await api.patchStage(stage.id, { displayMode: 'both' })
                    await onRefresh()
                  } catch { /* ignore */ } finally { setBusy(null) }
                }}
              >
                兩者 (Tab)
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* ── 4. 資料編輯 ──────────────────────────────────────────── */}
      <hr style={{ borderColor: '#223155', margin: '0.25rem 0' }} />
      <div className="stack" style={{ gap: '0.6rem' }}>

      {stage.kind === 'player_list' ? (
        <div className="stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="stage-section-title">📝 資料編輯</div>
            <div className="row" style={{ gap: '0.4rem' }}>
              <button type="button" className={editorMode === 'table' ? 'btn primary btn-sm' : 'btn btn-sm'} disabled={!!busy} onClick={() => setEditorMode('table')}>表格</button>
              <button type="button" className={editorMode === 'json' ? 'btn primary btn-sm' : 'btn btn-sm'} disabled={!!busy} onClick={() => setEditorMode('json')}>JSON</button>
              {editorMode === 'table' ? (
                <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => setPlayerRows((r) => [...r, { player_id: crypto.randomUUID().slice(0, 8), name: '', no: '', date: '', division: '' }])}>+ 新增一列</button>
              ) : null}
            </div>
          </div>
          <div className="muted">玩家數：{playerRows.length}</div>

          {editorMode === 'table' ? (
            <div className="table-wrap">
              <table className="table admin-edit-table">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>player_id</th>
                    <th>name</th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {playerRows.map((row, idx) => (
                    <tr key={`${row.player_id}-${idx}`}>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.player_id}
                          onChange={(e) =>
                            setPlayerRows((r) => r.map((x, i) => (i === idx ? { ...x, player_id: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.name}
                          onChange={(e) =>
                            setPlayerRows((r) => r.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={!!busy}
                          onClick={() => setPlayerRows((r) => r.filter((_, i) => i !== idx))}
                        >
                          刪
                        </button>
                      </td>
                    </tr>
                  ))}
                  {playerRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        （尚無資料）先按「解析（Vision）」或新增一列開始填
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : (
            <label className="stack">
              <span className="muted">JSON（可手動修正後儲存草稿）</span>
              <textarea className="code" value={jsonDraft} onChange={(e) => setJsonDraft(e.target.value)} />
            </label>
          )}
        </div>
      ) : stage.kind === 'pairings' ? (
        <div className="stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="stage-section-title">📝 資料編輯</div>
            <div className="row" style={{ gap: '0.4rem' }}>
              <button type="button" className={editorMode === 'table' ? 'btn primary btn-sm' : 'btn btn-sm'} disabled={!!busy} onClick={() => setEditorMode('table')}>表格</button>
              <button type="button" className={editorMode === 'json' ? 'btn primary btn-sm' : 'btn btn-sm'} disabled={!!busy} onClick={() => setEditorMode('json')}>JSON</button>
              {editorMode === 'table' ? (
                <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => setPairingRows((r) => [...r, { table: r.length ? Math.max(...r.map((x) => x.table || 0)) + 1 : 1, p1_id: '', p1_name: '', p1_seat_or_suffix: '', p1_division: '', p1_record: '', p2_id: '', p2_name: '', p2_seat_or_suffix: '', p2_division: '', p2_record: '', is_bye: false }])}>+ 新增一列</button>
              ) : null}
            </div>
          </div>
          <div className="muted">桌數：{pairingRows.length}；玩家數：{pairingRows.reduce((sum, row) => sum + (row.p1_id || row.p1_name ? 1 : 0) + (row.p2_id || row.p2_name ? 1 : 0), 0)}</div>

          {editorMode === 'table' ? (
            <div className="table-wrap">
              <table className="table admin-edit-table admin-edit-table-wide">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>table</th>
                    <th style={{ width: 130 }}>p1 id</th>
                    <th style={{ width: 180 }}>p1 name</th>
                    <th style={{ width: 90 }}>p1 seat</th>
                    <th style={{ width: 80 }}>p1 div</th>
                    <th style={{ width: 120 }}>p1 record</th>
                    <th style={{ width: 130 }}>p2 id</th>
                    <th style={{ width: 180 }}>p2 name</th>
                    <th style={{ width: 90 }}>p2 seat</th>
                    <th style={{ width: 80 }}>p2 div</th>
                    <th style={{ width: 120 }}>p2 record</th>
                    <th style={{ width: 70 }}>bye</th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {pairingRows.map((row, idx) => (
                    <tr key={`${row.table}-${idx}`}>
                      <td>
                        <input
                          className="input input-sm"
                          type="number"
                          value={Number.isFinite(row.table) ? row.table : 0}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, table: Number(e.target.value || 0) } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_id}
                          onChange={(e) =>
                            setPairingRows((r) => r.map((x, i) => (i === idx ? { ...x, p1_id: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_name}
                          onChange={(e) =>
                            setPairingRows((r) => r.map((x, i) => (i === idx ? { ...x, p1_name: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_seat_or_suffix ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p1_seat_or_suffix: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_division ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p1_division: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_record ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p1_record: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_id ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) => r.map((x, i) => (i === idx ? { ...x, p2_id: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_name ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) => r.map((x, i) => (i === idx ? { ...x, p2_name: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_seat_or_suffix ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p2_seat_or_suffix: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_division ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p2_division: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_record ?? ''}
                          onChange={(e) =>
                            setPairingRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p2_record: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!row.is_bye}
                          onChange={(e) =>
                            setPairingRows((r) => r.map((x, i) => (i === idx ? { ...x, is_bye: e.target.checked } : x)))
                          }
                        />
                      </td>
                      <td>
                        <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => setPairingRows((r) => r.filter((_, i) => i !== idx))}>
                          刪
                        </button>
                      </td>
                    </tr>
                  ))}
                  {pairingRows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="muted">
                        （尚無資料）先按「解析（Vision）」或新增一列開始填
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : (
            <label className="stack">
              <span className="muted">JSON（可手動修正後儲存草稿）</span>
              <textarea className="code" value={jsonDraft} onChange={(e) => setJsonDraft(e.target.value)} />
            </label>
          )}
        </div>
      ) : stage.kind === 'round_results' ? (
        <div className="stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="stage-section-title">📝 資料編輯</div>
            <div className="row" style={{ gap: '0.4rem' }}>
              <button type="button" className={editorMode === 'table' ? 'btn primary btn-sm' : 'btn btn-sm'} disabled={!!busy} onClick={() => setEditorMode('table')}>表格</button>
              <button type="button" className={editorMode === 'json' ? 'btn primary btn-sm' : 'btn btn-sm'} disabled={!!busy} onClick={() => setEditorMode('json')}>JSON</button>
              {editorMode === 'table' ? (
                <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => setResultRows((r) => [...r, { table: r.length ? Math.max(...r.map((x) => x.table || 0)) + 1 : 1, p1_id: '', p1_name: '', p1_record: '', p1_is_winner: undefined, p2_id: '', p2_name: '', p2_record: '', p2_is_winner: undefined, is_bye: false }])}>+ 新增一列</button>
              ) : null}
            </div>
          </div>
          <div className="round-workspace-toolbar">
            <div className="row" style={{ gap: '0.35rem' }}>
              <span className="round-stat-pill">總桌數 {resultRows.length}</span>
              <span className={`round-stat-pill ${resultAnomalyCount > 0 ? 'danger' : ''}`}>異常 {resultAnomalyCount}</span>
              <span className="round-stat-pill">雙敗 {resultDoubleLossCount}</span>
              {resultShowAnomaliesOnly ? <span className="round-stat-pill">顯示 {visibleResultRows.length}</span> : null}
            </div>
            <button
              type="button"
              className={resultShowAnomaliesOnly ? 'btn btn-sm primary' : 'btn btn-sm'}
              onClick={() => setResultShowAnomaliesOnly((v) => !v)}
            >
              {resultShowAnomaliesOnly ? '顯示全部' : '只看異常'}
            </button>
          </div>
          <div className="muted">桌數：{resultRows.length}；玩家數：{resultRows.reduce((sum, row) => sum + (row.p1_id || row.p1_name ? 1 : 0) + (row.p2_id || row.p2_name ? 1 : 0), 0)}</div>

          {editorMode === 'table' ? (
            <div className="table-wrap">
              <table className="table admin-edit-table admin-edit-table-wide">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>table</th>
                    <th style={{ width: 130 }}>p1 id</th>
                    <th style={{ width: 180 }}>p1 name</th>
                    <th style={{ width: 140 }}>p1 record</th>
                    <th style={{ width: 70 }}>p1 W</th>
                    <th style={{ width: 130 }}>p2 id</th>
                    <th style={{ width: 180 }}>p2 name</th>
                    <th style={{ width: 140 }}>p2 record</th>
                    <th style={{ width: 70 }}>p2 W</th>
                    <th style={{ width: 70 }}>bye</th>
                    <th style={{ width: 220 }}>檢查</th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {visibleResultRows.map(({ row, idx, issues, isDoubleLoss }) => (
                    <tr key={`${row.table}-${idx}`} className={issues.length > 0 ? 'round-anomaly-row' : isDoubleLoss ? 'round-double-loss-row' : undefined}>
                      <td>
                        <input
                          className="input input-sm"
                          type="number"
                          value={Number.isFinite(row.table) ? row.table : 0}
                          onChange={(e) =>
                            setResultRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, table: Number(e.target.value || 0) } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_id}
                          onChange={(e) =>
                            setResultRows((r) => r.map((x, i) => (i === idx ? { ...x, p1_id: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_name}
                          onChange={(e) =>
                            setResultRows((r) => r.map((x, i) => (i === idx ? { ...x, p1_name: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p1_record ?? ''}
                          onChange={(e) =>
                            setResultRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p1_record: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!row.p1_is_winner}
                          onChange={(e) =>
                            setResultRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p1_is_winner: e.target.checked } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_id ?? ''}
                          onChange={(e) =>
                            setResultRows((r) => r.map((x, i) => (i === idx ? { ...x, p2_id: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_name ?? ''}
                          onChange={(e) =>
                            setResultRows((r) => r.map((x, i) => (i === idx ? { ...x, p2_name: e.target.value } : x)))
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-sm"
                          value={row.p2_record ?? ''}
                          onChange={(e) =>
                            setResultRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p2_record: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!row.p2_is_winner}
                          onChange={(e) =>
                            setResultRows((r) =>
                              r.map((x, i) => (i === idx ? { ...x, p2_is_winner: e.target.checked } : x))
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!row.is_bye}
                          onChange={(e) =>
                            setResultRows((r) => r.map((x, i) => (i === idx ? { ...x, is_bye: e.target.checked } : x)))
                          }
                        />
                      </td>
                      <td>
                        {issues.length > 0 ? (
                          <div className="round-issue-badges">
                            {issues.map((issue) => (
                              <span key={issue} className="round-issue-badge">
                                {issue}
                              </span>
                            ))}
                          </div>
                        ) : isDoubleLoss ? (
                          <span className="round-double-loss-badge">雙敗（已確認）</span>
                        ) : (
                          <span className="muted">-</span>
                        )}
                      </td>
                      <td>
                        <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => setResultRows((r) => r.filter((_, i) => i !== idx))}>
                          刪
                        </button>
                      </td>
                    </tr>
                  ))}
                  {resultRows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="muted">
                        （尚無資料）先按「解析（Vision）」或新增一列開始填
                      </td>
                    </tr>
                  ) : null}
                  {resultRows.length > 0 && visibleResultRows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="muted">
                        目前沒有異常列。
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : (
            <label className="stack">
              <span className="muted">JSON（可手動修正後儲存草稿）</span>
              <textarea className="code" value={jsonDraft} onChange={(e) => setJsonDraft(e.target.value)} />
            </label>
          )}
        </div>
      ) : (
        <label className="stack">
          <span className="muted">JSON（可手動修正後儲存草稿）</span>
          <textarea className="code" value={jsonDraft} onChange={(e) => setJsonDraft(e.target.value)} />
        </label>
      )}

        {/* Save action */}
        <button
          className={`btn btn-sm primary ${busy === `patch:${stage.id}` ? 'busy' : ''}`}
          type="button"
          disabled={!!busy}
          onClick={async () => {
            setBusy(`patch:${stage.id}`)
            setMsg(null)
            try {
              let parsed: unknown
              if (stage.kind === 'player_list' && editorMode === 'table') {
                const trimmed = playerRows
                  .map((r) => ({
                    player_id: String(r.player_id ?? '').trim(),
                    name: String(r.name ?? '').trim(),
                    no: String(r.no ?? '').trim(),
                    date: String(r.date ?? '').trim(),
                    division: String(r.division ?? '').trim(),
                  }))
                  .filter((r) => r.player_id || r.name)
                const seen = new Set<string>()
                for (const r of trimmed) {
                  if (!r.player_id) throw new Error('player_id 不能為空')
                  if (!r.name) throw new Error('name 不能為空')
                  if (seen.has(r.player_id)) throw new Error(`player_id 重複：${r.player_id}`)
                  seen.add(r.player_id)
                }
                parsed = {
                  players: trimmed.map((r) => ({
                    player_id: r.player_id,
                    name: r.name,
                    ...(r.no ? { no: r.no } : {}),
                    ...(r.date ? { date: r.date } : {}),
                    ...(r.division ? { division: r.division } : {}),
                  })),
                }
                setJsonDraft(JSON.stringify(parsed, null, 2))
              } else if (stage.kind === 'pairings' && editorMode === 'table') {
                const trimmed = pairingRows
                  .map((r) => ({
                    table: Number(r.table ?? 0),
                    p1_id: String(r.p1_id ?? '').trim(),
                    p1_name: String(r.p1_name ?? '').trim(),
                    p1_seat_or_suffix: String(r.p1_seat_or_suffix ?? '').trim(),
                    p1_division: String(r.p1_division ?? '').trim(),
                    p1_record: String(r.p1_record ?? '').trim(),
                    p2_id: String(r.p2_id ?? '').trim(),
                    p2_name: String(r.p2_name ?? '').trim(),
                    p2_seat_or_suffix: String(r.p2_seat_or_suffix ?? '').trim(),
                    p2_division: String(r.p2_division ?? '').trim(),
                    p2_record: String(r.p2_record ?? '').trim(),
                    is_bye: !!r.is_bye,
                  }))
                  .filter((r) => r.table || r.p1_id || r.p1_name || r.p2_id || r.p2_name)
                const seen = new Set<number>()
                for (const r of trimmed) {
                  if (!Number.isFinite(r.table) || !Number.isInteger(r.table) || r.table < 0) {
                    throw new Error('table 必須為非負整數')
                  }
                  const byeLike = r.is_bye || (!r.p2_id && !r.p2_name)
                  if (byeLike && r.table !== 0) {
                    throw new Error(`table ${r.table}：BYE 必須使用 table 0`)
                  }
                  if (!byeLike && r.table <= 0) {
                    throw new Error(`table ${r.table}：非 BYE 對局 table 必須為正整數`)
                  }
                  if (!r.p1_id) throw new Error(`table ${r.table}：p1 id 不能為空`)
                  if (!r.p1_name) throw new Error(`table ${r.table}：p1 name 不能為空`)
                  if (seen.has(r.table)) throw new Error(`table 重複：${r.table}`)
                  seen.add(r.table)
                }
                parsed = {
                  round: stage.round ?? undefined,
                  pairings: trimmed
                    .sort((a, b) => a.table - b.table)
                    .map((r) => ({
                      table: r.table,
                      player1: {
                        id: r.p1_id,
                        name: r.p1_name,
                        ...(r.p1_seat_or_suffix ? { seat_or_suffix: r.p1_seat_or_suffix } : {}),
                        ...(r.p1_division ? { division: r.p1_division } : {}),
                        ...(r.p1_record ? { record: r.p1_record } : {}),
                      },
                      ...(r.p2_id || r.p2_name || r.p2_seat_or_suffix || r.p2_division || r.p2_record
                        ? {
                            player2: {
                              ...(r.p2_id ? { id: r.p2_id } : {}),
                              ...(r.p2_name ? { name: r.p2_name } : {}),
                              ...(r.p2_seat_or_suffix ? { seat_or_suffix: r.p2_seat_or_suffix } : {}),
                              ...(r.p2_division ? { division: r.p2_division } : {}),
                              ...(r.p2_record ? { record: r.p2_record } : {}),
                            },
                          }
                        : {}),
                      ...(r.is_bye ? { is_bye: true } : {}),
                    })),
                }
                setJsonDraft(JSON.stringify(parsed, null, 2))
              } else if (stage.kind === 'round_results' && editorMode === 'table') {
                const trimmed = resultRows
                  .map((r) => ({
                    table: Number(r.table ?? 0),
                    p1_id: String(r.p1_id ?? '').trim(),
                    p1_name: String(r.p1_name ?? '').trim(),
                    p1_record: String(r.p1_record ?? '').trim(),
                    p1_is_winner: r.p1_is_winner === undefined ? undefined : !!r.p1_is_winner,
                    p2_id: String(r.p2_id ?? '').trim(),
                    p2_name: String(r.p2_name ?? '').trim(),
                    p2_record: String(r.p2_record ?? '').trim(),
                    p2_is_winner: r.p2_is_winner === undefined ? undefined : !!r.p2_is_winner,
                    is_bye: !!r.is_bye,
                  }))
                  .filter((r) => r.table || r.p1_id || r.p1_name || r.p2_id || r.p2_name)
                const seen = new Set<number>()
                for (const r of trimmed) {
                  if (!Number.isFinite(r.table) || !Number.isInteger(r.table) || r.table < 0) {
                    throw new Error('table 必須為非負整數')
                  }
                  const byeLike = r.is_bye || (!r.p2_id && !r.p2_name)
                  if (byeLike && r.table !== 0) {
                    throw new Error(`table ${r.table}：BYE 必須使用 table 0`)
                  }
                  if (!byeLike && r.table <= 0) {
                    throw new Error(`table ${r.table}：非 BYE 對局 table 必須為正整數`)
                  }
                  if (!r.p1_id) throw new Error(`table ${r.table}：p1 id 不能為空`)
                  if (!r.p1_name) throw new Error(`table ${r.table}：p1 name 不能為空`)
                  if (seen.has(r.table)) throw new Error(`table 重複：${r.table}`)
                  seen.add(r.table)
                }
                parsed = {
                  round: stage.round ?? undefined,
                  matches: trimmed
                    .sort((a, b) => a.table - b.table)
                    .map((r) => ({
                      table: r.table,
                      player1: {
                        id: r.p1_id,
                        name: r.p1_name,
                        ...(r.p1_record ? { record: r.p1_record } : {}),
                        ...(r.p1_is_winner !== undefined ? { is_winner: r.p1_is_winner } : {}),
                      },
                      ...(r.p2_id || r.p2_name || r.p2_record || r.p2_is_winner !== undefined
                        ? {
                            player2: {
                              ...(r.p2_id ? { id: r.p2_id } : {}),
                              ...(r.p2_name ? { name: r.p2_name } : {}),
                              ...(r.p2_record ? { record: r.p2_record } : {}),
                              ...(r.p2_is_winner !== undefined ? { is_winner: r.p2_is_winner } : {}),
                            },
                          }
                        : {}),
                      ...(r.is_bye ? { is_bye: true } : {}),
                    })),
                }
                setJsonDraft(JSON.stringify(parsed, null, 2))
              } else {
                try {
                  parsed = JSON.parse(jsonDraft || 'null')
                } catch {
                  throw new Error('JSON 格式錯誤')
                }
              }
              await api.patchStage(stage.id, { parsedJson: parsed })
              setMsg('已儲存草稿')
              await onRefresh()
            } catch (err) {
              setMsg(err instanceof Error ? err.message : '儲存失敗')
            } finally {
              setBusy(null)
            }
          }}
        >
          {busy === `patch:${stage.id}` ? (
            <img src={LOADING_GIF} alt="Loading" style={{ width: 18, height: 18, imageRendering: 'pixelated' }} />
          ) : '💾 儲存草稿'}
        </button>
      </div> {/* end section 4 */}

      {/* ── Danger Zone ──────────────────────────────────────────── */}
      <hr style={{ borderColor: '#3f1a1a', margin: '0.25rem 0' }} />
      <div className="row" style={{ gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: '#6b2a2a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>⚠️ 危險操作</span>
        <button
          className={confirmDelete ? 'btn danger btn-sm' : 'btn btn-sm'}
          type="button"
          disabled={!!busy}
          onClick={async () => {
            if (!confirmDelete) {
              setConfirmDelete(true)
              setMsg('再按一次「確認刪除」就會永久刪除此階段')
              return
            }
            setBusy(`del:${stage.id}`)
            setMsg(null)
            try {
              await api.deleteStage(stage.id)
              await onRefresh()
            } catch (err) {
              setMsg(err instanceof Error ? err.message : '刪除失敗')
            } finally {
              setBusy(null)
              setConfirmDelete(false)
            }
          }}
        >
          {confirmDelete ? '確認刪除' : '刪除階段'}
        </button>
        {confirmDelete ? (
          <button
            className="btn"
            type="button"
            disabled={!!busy}
            onClick={() => {
              setConfirmDelete(false)
              setMsg(null)
            }}
          >
            取消
          </button>
        ) : null}
      </div>

      {preview ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreview(null)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700 }}>圖片預覽</div>
              <div className="row" style={{ gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn danger btn-sm"
                  disabled={!!busy}
                  onClick={async () => {
                    if (!confirm('確定要刪除這張圖片嗎？')) return
                    setBusy(`del_up:${preview.uploadId}`)
                    try {
                      await api.deleteUpload(stage.id, preview.uploadId)
                      setPreview(null)
                      await onRefresh()
                      const r = await api.stageUploads(stage.id)
                      setUploads(r.uploads.map((x) => ({ id: x.id, sortIndex: x.sortIndex })))
                      setMsg('圖片已刪除')
                    } catch (err) {
                      setMsg(err instanceof Error ? err.message : '刪除失敗')
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  刪除圖片
                </button>
                <button type="button" className="btn btn-sm" onClick={() => setPreview(null)}>
                  關閉
                </button>
              </div>
            </div>
            <img alt="上載圖片預覽" className="modal-img" src={`/api/stages/${stage.id}/uploads/${preview.uploadId}/image`} />
          </div>
        </div>
      ) : null}
        </>
      )} {/* end collapsible body */}
    </div>
  )
}
